import * as THREE from "three";
import {TDSLoader} from "three/examples/jsm/loaders/TDSLoader";

/** Целевой размер ручки по длинной стороне (мм), если пользователь не задал. */
export const DEFAULT_HANDLE_SIZE_MM = 120;

const templateCache = new Map();

export const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunk = 0x8000;
    let binary = "";
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
};

export const base64ToArrayBuffer = (base64) => {
    const binary = atob(String(base64 || ""));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
};

/**
 * Эвристика единиц 3DS → метры.
 * Крупные координаты считаем миллиметрами.
 */
const unitScaleToMeters = (maxDim) => {
    if (!Number.isFinite(maxDim) || maxDim <= 0) return 1;
    if (maxDim > 5) return 0.001;
    if (maxDim > 0.5) return 0.01;
    return 1;
};

/**
 * @param {ArrayBuffer} arrayBuffer
 * @returns {{
 *   template: THREE.Group,
 *   sizeMeters: {x:number,y:number,z:number},
 *   maxDimMeters: number,
 *   meshCount: number,
 * }}
 */
export const parse3dsArrayBuffer = (arrayBuffer) => {
    if (!arrayBuffer || !(arrayBuffer.byteLength > 0)) {
        throw new Error("Пустой 3DS-файл");
    }

    const loader = new TDSLoader();
    let parsed;
    try {
        parsed = loader.parse(arrayBuffer, "");
    } catch (err) {
        throw new Error(err?.message || "Не удалось разобрать 3DS");
    }
    if (!parsed) {
        throw new Error("Не удалось разобрать 3DS");
    }

    let meshCount = 0;
    parsed.traverse((obj) => {
        if (obj.isMesh) meshCount += 1;
    });
    if (meshCount === 0) {
        throw new Error("В 3DS нет геометрии (mesh)");
    }

    const box = new THREE.Box3().setFromObject(parsed);
    if (box.isEmpty()) {
        throw new Error("Не удалось определить габариты модели");
    }

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    const maxDim = Math.max(size.x, size.y, size.z, 1e-6);
    const toMeters = unitScaleToMeters(maxDim);

    // position не масштабируется собственным scale → смещение центра = -center * toMeters
    const inner = parsed;
    inner.scale.multiplyScalar(toMeters);
    inner.position.copy(center).multiplyScalar(-toMeters);
    inner.updateMatrixWorld(true);

    // Видимый материал (у части 3DS нет нормалей / чёрный phong)
    let previewColor = null;
    let previewMetalness = 0.45;
    let previewRoughness = 0.4;
    let previewHasMap = false;
    let previewMapUrl = null;

    const tryTextureDataUrl = (texture) => {
        try {
            const img = texture?.image;
            if (!img) return null;
            const w = img.width || img.videoWidth || 0;
            const h = img.height || img.videoHeight || 0;
            if (!(w > 0 && h > 0)) return null;
            const canvas = document.createElement("canvas");
            const side = 64;
            canvas.width = side;
            canvas.height = side;
            const ctx = canvas.getContext("2d");
            if (!ctx) return null;
            ctx.drawImage(img, 0, 0, side, side);
            return canvas.toDataURL("image/jpeg", 0.7);
        } catch {
            return null;
        }
    };

    inner.traverse((obj) => {
        if (!obj.isMesh) return;
        if (obj.geometry && !obj.geometry.getAttribute("normal")) {
            obj.geometry.computeVertexNormals();
        }
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        const next = mats.map((mat) => {
            if (!mat) {
                if (!previewColor) previewColor = "#B0B0B0";
                return new THREE.MeshStandardMaterial({
                    color: 0xb0b0b0,
                    metalness: 0.55,
                    roughness: 0.35,
                });
            }
            const color = mat.color ? mat.color.clone() : new THREE.Color(0xb0b0b0);
            if (color.r + color.g + color.b < 0.05) {
                color.setHex(0xb0b0b0);
            }
            if (!previewColor) {
                previewColor = `#${color.getHexString()}`;
                if ("metalness" in mat && Number.isFinite(mat.metalness)) {
                    previewMetalness = mat.metalness;
                }
                if ("roughness" in mat && Number.isFinite(mat.roughness)) {
                    previewRoughness = mat.roughness;
                }
                if (mat.map) {
                    previewHasMap = true;
                    previewMapUrl = tryTextureDataUrl(mat.map);
                }
            }
            return new THREE.MeshStandardMaterial({
                color,
                metalness: 0.45,
                roughness: 0.4,
                map: mat.map || null,
                side: THREE.DoubleSide,
            });
        });
        obj.material = Array.isArray(obj.material) ? next : next[0];
        obj.castShadow = false;
        obj.receiveShadow = false;
    });

    const template = new THREE.Group();
    template.name = "Handle3dsTemplate";
    template.add(inner);

    const sizeMeters = {
        x: size.x * toMeters,
        y: size.y * toMeters,
        z: size.z * toMeters,
    };
    const maxDimMeters = Math.max(sizeMeters.x, sizeMeters.y, sizeMeters.z, 1e-6);

    const materialPreview = {
        color: previewColor || "#B0B0B0",
        metalness: previewMetalness,
        roughness: previewRoughness,
        hasMap: previewHasMap,
        mapUrl: previewMapUrl,
    };

    return {template, sizeMeters, maxDimMeters, meshCount, materialPreview};
};

export const get3dsTemplate = (modelBase64) => {
    // v2: исправлено центрирование при scale (старый кэш давал смещение на метры)
    const key = `v2:${String(modelBase64 || "").length}:${String(modelBase64 || "").slice(0, 48)}`;
    if (templateCache.has(key)) {
        return templateCache.get(key);
    }
    const buffer = base64ToArrayBuffer(modelBase64);
    const parsed = parse3dsArrayBuffer(buffer);
    templateCache.set(key, parsed);
    return parsed;
};

/** Превью материала 3DS (цвет / карта) для иконки «Из файла». */
export const getHandleMaterialPreviewFromBase64 = (modelBase64) => {
    if (!modelBase64) return null;
    try {
        const {materialPreview} = get3dsTemplate(modelBase64);
        return materialPreview || null;
    } catch {
        return null;
    }
};

/** Клон шаблона с масштабом под длинную сторону sizeMm. */
export const createHandleObject = (modelBase64, sizeMm = DEFAULT_HANDLE_SIZE_MM) => {
    const {template, maxDimMeters} = get3dsTemplate(modelBase64);
    const target = Math.max(Number(sizeMm) || DEFAULT_HANDLE_SIZE_MM, 1) * 0.001;
    const scale = target / maxDimMeters;
    const clone = template.clone(true);
    // clone() шарит material между экземплярами — копируем, иначе смена цвета идёт на все ручки
    clone.traverse((obj) => {
        if (!obj.isMesh || !obj.material) return;
        if (Array.isArray(obj.material)) {
            obj.material = obj.material.map((mat) => (mat?.clone ? mat.clone() : mat));
        } else if (obj.material.clone) {
            obj.material = obj.material.clone();
        }
    });
    clone.scale.multiplyScalar(scale);
    clone.updateMatrixWorld(true);
    return clone;
};

/**
 * Прочитать File → данные для каталога ручек.
 */
export const load3dsHandleFromFile = async (file) => {
    const buffer = await file.arrayBuffer();
    if (buffer.byteLength > 8 * 1024 * 1024) {
        throw new Error("Файл больше 8 МБ — слишком большой для локального хранения");
    }
    const parsed = parse3dsArrayBuffer(buffer);
    const modelBase64 = arrayBufferToBase64(buffer);
    return {
        modelBase64,
        meshCount: parsed.meshCount,
        sizeMeters: parsed.sizeMeters,
        maxDimMeters: parsed.maxDimMeters,
        defaultSizeMm: DEFAULT_HANDLE_SIZE_MM,
        materialPreview: parsed.materialPreview,
    };
};

export const isHandleDetail = (detail) =>
    detail?.source === "3ds" || detail?.kind === "handle";
